import React, { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useConfig } from "../context/ConfigContext";
import { api } from "../lib/api";
import { alertCount } from "../lib/haccp";
import SyncIndicator from "./SyncIndicator";
import NotificationBell from "./NotificationBell";
import RenewGate from "./RenewGate";
import {
  LayoutDashboard, ShoppingCart, CalendarDays, Truck, ScanLine,
  Package, FileText, Settings, LogOut, Menu, X, Anchor, CalendarClock, Scale, ChefHat, ClipboardList, Users, Sailboat, UtensilsCrossed, Sparkles, UserRound, Inbox, Recycle,
} from "lucide-react";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/acquisti", label: "Acquisti", icon: ShoppingCart },
  { to: "/coperti", label: "Coperti", icon: CalendarDays },
  { to: "/charter", label: "Charter", icon: Sailboat },
  { to: "/menu", label: "Menù", icon: UtensilsCrossed },
  { to: "/ospiti", label: "Ospiti", icon: UserRound },
  { to: "/fornitori", label: "Fornitori", icon: Truck },
  { to: "/ordini", label: "Ordini", icon: ClipboardList },
  { to: "/prezzi", label: "Confronto Prezzi", icon: Scale },
  { to: "/ricette", label: "Ricette", icon: ChefHat },
  { to: "/scan", label: "Scansione AI", icon: ScanLine },
  { to: "/scadenze", label: "Scadenze", icon: CalendarClock },
  { to: "/marpol", label: "MARPOL", icon: Recycle },
  { to: "/giacenze", label: "Giacenze", icon: Package },
  { to: "/report", label: "Report", icon: FileText },
  { to: "/setup", label: "Impostazioni", icon: Settings },
];

const ROLE_LABEL = { chef: "Chef", comandante: "Comandante", armatore: "Armatore", stewardess: "Stewardess" };

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { config } = useConfig();
  const [open, setOpen] = useState(false);
  const [haccpAlerts, setHaccpAlerts] = useState(0);
  const [sub, setSub] = useState(null);
  const navigate = useNavigate();
  const yachtName = config?.name || "YACHT FOOD COST";
  const isManager = user?.role === "chef" || user?.role === "comandante";
  const navItems = (isManager
    ? [...NAV.slice(0, -1), { to: "/clienti", label: "Clienti", icon: Sparkles }, { to: "/contatti", label: "Contatti", icon: Inbox }, { to: "/utenti", label: "Utenti", icon: Users }, NAV[NAV.length - 1]]
    : NAV
  ).filter((item) => !(user?.role === "stewardess" && item.to === "/marpol"));

  useEffect(() => {
    const warn = config?.haccp_warn_days ?? 7;
    const load = () => api.get("/haccp").then((r) => setHaccpAlerts(alertCount(r.data, warn))).catch(() => {});
    load();
    const iv = setInterval(load, 60000);
    return () => clearInterval(iv);
  }, [config?.haccp_warn_days]);

  useEffect(() => {
    api.get("/subscription").then((r) => setSub(r.data)).catch(() => {});
  }, []);

  const [newRequests, setNewRequests] = useState(0);
  useEffect(() => {
    if (!isManager) return;
    const load = () => api.get("/client-requests").then((r) => setNewRequests(r.data.filter((x) => x.status === "nuova").length)).catch(() => {});
    load();
    const iv = setInterval(load, 60000);
    return () => clearInterval(iv);
  }, [isManager]);

  const [newLeads, setNewLeads] = useState(0);
  useEffect(() => {
    if (!isManager) return;
    const load = () => api.get("/leads").then((r) => setNewLeads(r.data.filter((x) => !x.status || x.status === "nuova").length)).catch(() => {});
    load();
    const iv = setInterval(load, 60000);
    return () => clearInterval(iv);
  }, [isManager]);

  const navContent = (
    <nav className="flex flex-col gap-1 px-3">
      {navItems.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
          onClick={() => setOpen(false)}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-lg px-4 py-3 text-[15px] font-medium transition-colors ${
              isActive ? "bg-gold text-black" : "text-slate-200 hover:bg-white/10"
            }`
          }
        >
          <item.icon className="h-5 w-5 shrink-0" />
          <span className="flex-1">{item.label}</span>
          {item.to === "/scadenze" && haccpAlerts > 0 && (
            <span data-testid="scadenze-badge" className="flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 text-xs font-bold text-white">
              {haccpAlerts}
            </span>
          )}
          {item.to === "/clienti" && newRequests > 0 && (
            <span data-testid="clienti-badge" className="flex h-5 min-w-5 items-center justify-center rounded-full bg-gold px-1.5 text-xs font-bold text-black">
              {newRequests}
            </span>
          )}
          {item.to === "/contatti" && newLeads > 0 && (
            <span data-testid="contatti-badge" className="flex h-5 min-w-5 items-center justify-center rounded-full bg-gold px-1.5 text-xs font-bold text-black">
              {newLeads}
            </span>
          )}
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div className="flex min-h-screen bg-[#F4F1EA]">
      {/* Desktop sidebar */}
      <aside className="hidden w-64 shrink-0 flex-col bg-navy py-5 md:flex">
        <div className="mb-6 flex items-center gap-3 px-6">
          <img src="/logo.png" alt="logo" className="h-11 w-11 rounded-lg" />
          <div>
            <div className="font-head text-lg font-bold leading-none text-white">{yachtName}</div>
            <div className="text-[11px] text-gold">Yacht Food Cost</div>
          </div>
        </div>
        {navContent}
        <div className="mt-auto px-5 pt-6">
          {config?.sandbox && (
            <div className="mb-3 rounded-md bg-amber-500/20 px-3 py-1.5 text-center text-[11px] font-semibold text-amber-300">
              MODALITÀ SANDBOX
            </div>
          )}
          <div className="mb-2 text-sm text-slate-300">
            <div className="font-semibold text-white">{user?.name}</div>
            <div className="text-xs text-gold">{ROLE_LABEL[user?.role]}</div>
          </div>
          <button
            data-testid="logout-btn"
            onClick={logout}
            className="flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-sm text-slate-300 hover:bg-white/10"
          >
            <LogOut className="h-4 w-4" /> Esci
          </button>
        </div>
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <aside className="animate-slide-up absolute left-0 top-0 flex h-full w-72 flex-col bg-navy py-5">
            <div className="mb-6 flex items-center justify-between px-6">
              <div className="flex items-center gap-2">
                <img src="/logo.png" alt="logo" className="h-9 w-9 rounded-lg" />
                <span className="font-head font-bold text-white">{yachtName}</span>
              </div>
              <button data-testid="close-menu" onClick={() => setOpen(false)}>
                <X className="h-6 w-6 text-white" />
              </button>
            </div>
            {navContent}
            <div className="mt-auto px-5">
              <button onClick={logout} className="flex w-full items-center gap-2 rounded-lg px-3 py-3 text-slate-300 hover:bg-white/10">
                <LogOut className="h-4 w-4" /> Esci ({ROLE_LABEL[user?.role]})
              </button>
            </div>
          </aside>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Top bar */}
        <header className="sticky top-0 z-30 flex items-center justify-between gap-3 border-b border-slate-200 bg-navy px-4 py-3 md:bg-[#F4F1EA] md:px-8">
          <div className="flex items-center gap-3">
            <button
              data-testid="open-menu"
              className="md:hidden"
              onClick={() => setOpen(true)}
            >
              <Menu className="h-6 w-6 text-white" />
            </button>
            <div className="flex items-center gap-2 md:hidden">
              <Anchor className="h-5 w-5 text-gold" />
              <span className="font-head font-bold text-white">{yachtName}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <NotificationBell />
            <SyncIndicator />
          </div>
        </header>
        {sub && sub.remind && !sub.blocked && (
          <div data-testid="sub-banner" className="flex flex-wrap items-center justify-between gap-2 bg-gold/20 px-4 py-2.5 text-sm text-navy md:px-8">
            <span className="font-medium">
              {sub.in_grace
                ? `Abbonamento scaduto — hai ancora pochi giorni per rinnovare senza interruzioni.`
                : `Il tuo abbonamento scade tra ${sub.days_left} giorn${sub.days_left === 1 ? "o" : "i"}.`}
            </span>
            <button
              data-testid="sub-banner-renew"
              onClick={() => api.post("/payments/checkout", { origin_url: window.location.origin }).then((r) => (window.location.href = r.data.checkout_url)).catch(() => {})}
              className="rounded-full bg-navy px-4 py-1.5 text-xs font-bold text-white hover:bg-navy-hover"
            >
              Rinnova ora
            </button>
          </div>
        )}
        <main className="min-w-0 flex-1 p-4 md:p-8">{sub && sub.blocked ? <RenewGate sub={sub} /> : children}</main>
      </div>
    </div>
  );
}
